import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowLeft,
  Mail,
  MapPin,
  Send,
  Globe,
  Linkedin,
  Building2,
  BadgeInfo,
  Sparkles,
  Clock,
  CheckCircle2,
} from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { useLang } from "@/contexts/LanguageContext";

const Contact = () => {
  const { t } = useLang();
  const c = t.contact;
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => window.scrollTo(0, 0), []);

  const onSubmit = (e) => {
    e.preventDefault();
    setSubmitted(true);
  };

  return (
    <div data-testid="contact-page" className="min-h-screen bg-emka">
      <Header />

      {/* Hero */}
      <section className="pt-[150px] lg:pt-[170px] pb-8">
        <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-10">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-white/75 hover:text-white text-xs font-semibold tracking-widest uppercase mb-5"
          >
            <ArrowLeft size={14} />
            {t.category.backHome}
          </Link>
          <p
            className="text-[12px] font-bold tracking-[0.32em] uppercase"
            style={{ color: "var(--emka-gold)" }}
          >
            {t.nav.contact}
          </p>
          <h1
            className="mt-3 font-display font-extrabold text-white leading-tight"
            style={{ fontSize: "clamp(2rem, 4vw, 3rem)" }}
          >
            {c.heroTitle}
          </h1>
          <p className="mt-4 text-white/70 max-w-2xl leading-relaxed">
            {c.heroIntro}
          </p>
          <span className="gold-divider mt-6" />
        </div>
      </section>

      {/* Main contact grid */}
      <section className="pb-16">
        <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-10 grid lg:grid-cols-5 gap-6 lg:gap-8 items-start">
          {/* LEFT — info column */}
          <aside
            data-testid="contact-info-panel"
            className="lg:col-span-2 lg:sticky lg:top-32 space-y-5"
          >
            {/* Company card */}
            <div
              className="rounded-lg overflow-hidden border border-white/10"
              style={{ backgroundColor: "#0c1330" }}
              data-testid="company-card"
            >
              <div
                className="h-1 w-full"
                style={{ backgroundColor: "var(--emka-gold)" }}
              />
              <div className="p-6 sm:p-7">
                <div className="flex items-center gap-3 mb-1">
                  <Building2
                    size={20}
                    style={{ color: "var(--emka-gold)" }}
                  />
                  <h2 className="text-white font-display font-extrabold text-lg leading-tight">
                    EMKA METAL LTD
                  </h2>
                </div>
                <p className="text-white/55 text-[11px] tracking-[0.22em] uppercase mb-5 pl-8">
                  {c.companySubtitle}
                </p>

                <dl className="space-y-3 text-sm">
                  <div className="flex gap-3">
                    <MapPin
                      size={15}
                      className="mt-0.5 shrink-0"
                      style={{ color: "var(--emka-gold)" }}
                    />
                    <div>
                      <dt className="text-[10px] uppercase tracking-[0.22em] text-white/45 mb-0.5">
                        {c.labels.address}
                      </dt>
                      <dd className="text-white/90 leading-snug">
                        Banishora 3 &quot;Krusha Planin&quot; Str.
                        <br />
                        Sofia / Bulgaria
                      </dd>
                    </div>
                  </div>
                  <div className="flex gap-3">
                    <BadgeInfo
                      size={15}
                      className="mt-0.5 shrink-0"
                      style={{ color: "var(--emka-gold)" }}
                    />
                    <div>
                      <dt className="text-[10px] uppercase tracking-[0.22em] text-white/45 mb-0.5">
                        {c.labels.vat}
                      </dt>
                      <dd className="text-white/90 font-mono">BG208518840</dd>
                    </div>
                  </div>
                  <div className="flex gap-3">
                    <Mail
                      size={15}
                      className="mt-0.5 shrink-0"
                      style={{ color: "var(--emka-gold)" }}
                    />
                    <div>
                      <dt className="text-[10px] uppercase tracking-[0.22em] text-white/45 mb-0.5">
                        {c.labels.sales}
                      </dt>
                      <dd>
                        <a
                          href="mailto:sales@emkagroup.eu"
                          className="text-white/90 hover:text-white"
                        >
                          sales@emkagroup.eu
                        </a>
                      </dd>
                    </div>
                  </div>
                  <div className="flex gap-3">
                    <Globe
                      size={15}
                      className="mt-0.5 shrink-0"
                      style={{ color: "var(--emka-gold)" }}
                    />
                    <div>
                      <dt className="text-[10px] uppercase tracking-[0.22em] text-white/45 mb-0.5">
                        {c.labels.web}
                      </dt>
                      <dd>
                        <a
                          href="https://www.emkagroup.eu"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-white/90 hover:text-white"
                        >
                          www.emkagroup.eu
                        </a>
                      </dd>
                    </div>
                  </div>
                </dl>

                <div className="mt-6 pt-5 border-t border-white/8">
                  <p
                    className="text-[10px] font-bold tracking-[0.28em] uppercase mb-2"
                    style={{ color: "var(--emka-gold)" }}
                  >
                    {c.precisionEyebrow}
                  </p>
                  <p className="text-white/70 text-xs leading-relaxed">
                    {c.precisionBody}
                  </p>
                </div>
              </div>
            </div>

            {/* Person card */}
            <div
              className="rounded-lg border border-white/10 p-6"
              style={{ backgroundColor: "#0c1330" }}
              data-testid="person-card"
            >
              <div className="flex items-start gap-4">
                <div
                  className="h-12 w-12 rounded-full flex items-center justify-center font-display font-extrabold text-lg shrink-0"
                  style={{
                    backgroundColor: "var(--emka-gold-soft)",
                    color: "var(--emka-gold)",
                  }}
                >
                  KA
                </div>
                <div className="min-w-0 flex-1">
                  <h3 className="text-white font-semibold text-base leading-tight">
                    Kemal Aras
                  </h3>
                  <p className="text-white/60 text-xs mt-0.5">
                    {c.personRole}
                  </p>
                  <p className="text-white/45 text-[11px]">
                    {c.personCompany}
                  </p>
                </div>
              </div>
              <div className="mt-4 flex flex-wrap gap-2">
                <a
                  href="mailto:kemalaras@emkagroup.eu"
                  data-testid="person-email"
                  className="inline-flex items-center gap-2 rounded-full border border-white/12 px-3 py-1.5 text-[12px] text-white/85 hover:text-white hover:border-white/30 transition-colors"
                >
                  <Mail size={12} style={{ color: "var(--emka-gold)" }} />
                  {c.labels.email}
                </a>
                <a
                  href="https://wa.me/905322881693"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="person-whatsapp"
                  className="inline-flex items-center gap-2 rounded-full border border-white/12 px-3 py-1.5 text-[12px] text-white/85 hover:text-white hover:border-white/30 transition-colors"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                    width="12"
                    height="12"
                    fill="#25D366"
                    aria-hidden="true"
                  >
                    <path d="M12.04 2C6.58 2 2.13 6.45 2.13 11.91c0 2.1.55 4.14 1.6 5.94L2 22l4.28-1.12a9.9 9.9 0 0 0 5.76 1.83c5.46 0 9.91-4.45 9.91-9.91S17.5 2 12.04 2Zm4.5 12c-.25-.12-1.46-.72-1.69-.8-.23-.08-.4-.12-.56.12-.17.25-.65.8-.79.97-.15.17-.29.19-.54.06-.25-.12-1.05-.39-2-1.23-.74-.66-1.24-1.47-1.38-1.72-.15-.25-.02-.38.11-.5.11-.11.25-.29.37-.44.12-.15.17-.25.25-.41.08-.17.04-.31-.02-.44-.06-.12-.56-1.35-.77-1.85-.2-.48-.4-.42-.56-.42h-.48c-.17 0-.44.06-.67.31-.23.25-.87.85-.87 2.08 0 1.23.9 2.42 1.02 2.58.12.17 1.77 2.71 4.29 3.8 2.51 1.09 2.51.73 2.97.68.46-.05 1.46-.6 1.67-1.18.2-.58.2-1.07.14-1.18Z" />
                  </svg>
                  WhatsApp
                </a>
                <a
                  href="https://www.linkedin.com/in/kemal-aras-274581144"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="person-linkedin"
                  className="inline-flex items-center gap-2 rounded-full border border-white/12 px-3 py-1.5 text-[12px] text-white/85 hover:text-white hover:border-white/30 transition-colors"
                >
                  <Linkedin size={12} style={{ color: "var(--emka-gold)" }} />
                  {c.labels.linkedin}
                </a>
              </div>
            </div>

            {/* Quick facts */}
            <div
              className="rounded-lg border border-white/10 p-5"
              style={{ backgroundColor: "#0c1330" }}
            >
              <ul className="space-y-2.5 text-[13px] text-white/85">
                <li className="flex items-center gap-2.5">
                  <Clock size={14} style={{ color: "var(--emka-gold)" }} />
                  {c.quickFacts.response}
                </li>
                <li className="flex items-center gap-2.5">
                  <MapPin size={14} style={{ color: "var(--emka-gold)" }} />
                  {c.quickFacts.location}
                </li>
                <li className="flex items-center gap-2.5">
                  <Sparkles size={14} style={{ color: "var(--emka-gold)" }} />
                  {c.quickFacts.support}
                </li>
              </ul>
            </div>
          </aside>

          {/* RIGHT — form */}
          <div className="lg:col-span-3">
            <div
              className="rounded-lg border border-white/10 overflow-hidden"
              style={{ backgroundColor: "#0c1330" }}
            >
              <div
                className="h-1 w-full"
                style={{ backgroundColor: "var(--emka-gold)" }}
              />
              <div className="p-6 sm:p-8 lg:p-10">
                <div className="mb-8">
                  <p
                    className="text-[11px] font-bold tracking-[0.28em] uppercase"
                    style={{ color: "var(--emka-gold)" }}
                  >
                    {c.formEyebrow}
                  </p>
                  <h2 className="mt-2 font-display font-extrabold text-white text-2xl sm:text-3xl leading-tight">
                    {c.formTitle}
                  </h2>
                  <p className="mt-2 text-white/60 text-sm">
                    {c.formSubtitle}
                  </p>
                </div>

                {submitted ? (
                  <div
                    data-testid="contact-success"
                    className="text-center py-10"
                  >
                    <div
                      className="mx-auto mb-4 h-14 w-14 rounded-full flex items-center justify-center"
                      style={{ backgroundColor: "var(--emka-gold-soft)" }}
                    >
                      <CheckCircle2
                        size={24}
                        style={{ color: "var(--emka-gold)" }}
                      />
                    </div>
                    <h3 className="text-white text-xl font-semibold">
                      {c.successTitle}
                    </h3>
                    <p className="text-white/70 text-sm mt-2 max-w-md mx-auto">
                      {c.successBody}
                    </p>
                    <button
                      type="button"
                      onClick={() => setSubmitted(false)}
                      className="mt-6 inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-white/70 hover:text-white"
                    >
                      {c.sendAnother}
                    </button>
                  </div>
                ) : (
                  <form onSubmit={onSubmit} data-testid="contact-form">
                    <div className="grid sm:grid-cols-2 gap-x-5 gap-y-5">
                      <div className="field">
                        <label className="field-label" htmlFor="c-name">
                          {c.fields.name}
                          <span className="text-[var(--emka-gold)]">*</span>
                        </label>
                        <input
                          id="c-name"
                          required
                          data-testid="contact-name"
                          type="text"
                          placeholder={c.fields.namePh}
                          className="field-input"
                        />
                      </div>
                      <div className="field">
                        <label className="field-label" htmlFor="c-company">
                          {c.fields.company}
                          <span className="text-[var(--emka-gold)]">*</span>
                        </label>
                        <input
                          id="c-company"
                          required
                          data-testid="contact-company"
                          type="text"
                          placeholder={c.fields.companyPh}
                          className="field-input"
                        />
                      </div>
                      <div className="field">
                        <label className="field-label" htmlFor="c-email">
                          {c.fields.email}
                          <span className="text-[var(--emka-gold)]">*</span>
                        </label>
                        <input
                          id="c-email"
                          required
                          data-testid="contact-email"
                          type="email"
                          placeholder={c.fields.emailPh}
                          className="field-input"
                        />
                      </div>
                      <div className="field">
                        <label className="field-label" htmlFor="c-phone">
                          {c.fields.phone}{" "}
                          <span className="text-white/40 normal-case tracking-normal">
                            ({c.fields.optional})
                          </span>
                        </label>
                        <input
                          id="c-phone"
                          data-testid="contact-phone"
                          type="tel"
                          placeholder={c.fields.phonePh}
                          className="field-input"
                        />
                      </div>

                      <div className="field sm:col-span-2">
                        <label className="field-label" htmlFor="c-topic">
                          {c.fields.interest}
                        </label>
                        <select
                          id="c-topic"
                          data-testid="contact-topic"
                          defaultValue=""
                          className="field-input"
                        >
                          <option value="" disabled>
                            {c.fields.interestPh}
                          </option>
                          <option value="metal">EMKA Metal</option>
                          <option value="urban">EMKA Urban</option>
                          <option value="plastics">EMKA Plastics</option>
                          <option value="packaging">EMKA Packaging</option>
                          <option value="lighting">EMKA Lighting</option>
                          <option value="general">
                            {c.fields.generalEnquiry}
                          </option>
                        </select>
                      </div>

                      <div className="field sm:col-span-2">
                        <label className="field-label" htmlFor="c-message">
                          {c.fields.message}
                          <span className="text-[var(--emka-gold)]">*</span>
                        </label>
                        <textarea
                          id="c-message"
                          required
                          data-testid="contact-message"
                          rows={6}
                          placeholder={c.fields.messagePh}
                          className="field-input resize-none"
                        />
                      </div>

                      <div className="sm:col-span-2 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 pt-2">
                        <p className="text-[11px] text-white/50 max-w-sm leading-relaxed">
                          {c.privacy}
                        </p>
                        <button
                          type="submit"
                          data-testid="contact-submit"
                          className="inline-flex items-center justify-center gap-2 px-6 py-3 font-semibold uppercase tracking-[0.18em] text-sm text-neutral-900 hover:brightness-95 transition-all"
                          style={{ backgroundColor: "var(--emka-gold)" }}
                        >
                          <Send size={16} />
                          {c.submit}
                        </button>
                      </div>
                    </div>
                  </form>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>
      <Footer />
    </div>
  );
};

export default Contact;
